import { createSelector } from '@reduxjs/toolkit';
import { selectOnlySelectedMappedFields } from 'app/containers/Map/slice/selectors';
import i18next from 'i18next';

import { RootState } from 'types';
import { getAreaWithUnits } from 'utils/helpers';
import { initialState } from '.';
import { getResourceOptions } from './helpers';
import {
  IAreaByCompanyListItem,
  IAreaByCultureListItem,
  ITaskAvailableResourceOption,
  ITaskResourceGroupedByCompanyItem,
} from './types';

const selectSlice = (state: RootState) => state.task || initialState;
export const selectTask = createSelector([selectSlice], state => state);

export const selectTaskRecipe = createSelector(
  [selectTask],
  state => state.taskDetails.recipe,
);

export const selectTaskFieldsCulturesIds = createSelector(
  [selectOnlySelectedMappedFields],
  fields =>
    Array.from(new Set(fields.map(field => field.fieldParameters.cultureId))),
);

export const selectTaskCategories = createSelector(
  [selectTask],
  state => state.categories,
);

export const selectTaskListState = createSelector(
  [selectTask],
  state => state.taskList,
);

export const selectSingleFieldTaskListState = createSelector(
  [selectTask, selectTaskListState],
  (state, taskList) => ({
    ...state.singleFieldTaskList,
    loading: taskList.loading,
  }),
);

export const selectTaskDetailsState = createSelector(
  [selectTask],
  state => state.taskDetails,
);

export const selectTaskDetailsAreaByCompanies = createSelector(
  [selectOnlySelectedMappedFields],
  fields => {
    const data = fields
      .reduce((accumulator: IAreaByCompanyListItem[], field) => {
        const companyIndex = accumulator.findIndex(
          data => data.companyId === field.fieldParameters.companyId,
        );
        if (companyIndex === -1) {
          accumulator.push({
            companyId: field.fieldParameters.companyId,
            companyName: field.fieldParameters.companyName,
            area: field.fieldParameters.areaNum,
            readableArea: '',
          });
        } else {
          accumulator[companyIndex] = {
            ...accumulator[companyIndex],
            area:
              accumulator[companyIndex].area + field.fieldParameters.areaNum,
          };
        }
        return accumulator;
      }, [])
      .map(companyArea => ({
        ...companyArea,
        readableArea: getAreaWithUnits(companyArea.area),
      }));

    return data;
  },
);

export const selectTaskDetailsAreaByCultures = createSelector(
  [selectOnlySelectedMappedFields],
  fields => {
    const data = fields
      .reduce((accumulator: IAreaByCultureListItem[], field) => {
        const cultureIndex = accumulator.findIndex(
          data => data.cultureId === (field.fieldParameters.cultureId || 0),
        );
        if (cultureIndex === -1) {
          accumulator.push({
            cultureId: field.fieldParameters.cultureId || 0,
            cultureName: field.fieldParameters.cultureName || '',
            area: field.fieldParameters.areaNum,
            readableArea: '',
          });
        } else {
          accumulator[cultureIndex] = {
            ...accumulator[cultureIndex],
            area:
              accumulator[cultureIndex].area + field.fieldParameters.areaNum,
          };
        }
        return accumulator;
      }, [])
      .map(cultureArea => ({
        ...cultureArea,
        cultureName:
          cultureArea.cultureName ||
          i18next?.t('Common.WithoutCulture', 'Without culture'),
        readableArea: getAreaWithUnits(cultureArea.area),
      }));

    return data;
  },
);

export const selectTaskProgressState = createSelector(
  [selectTask],
  state => state.taskProgress,
);
export const selectCreateTaskState = createSelector(
  [selectTask],
  state => state.createTask,
);

export const selectTaskListFilters = createSelector(
  [selectTask],
  state => state.taskList.filters,
);

export const selectFieldTaskListFilters = createSelector(
  [selectTask],
  state => state.singleFieldTaskList.filters,
);

export const selectTaskResourcesState = createSelector(
  [selectTask],
  state => state.taskResources,
);
export const selectUpdatedPlannedResourcesArr = createSelector(
  [selectTaskResourcesState],
  taskResources => taskResources.plannedResources.updatedResourcesArr,
);

export const selectThisStageUsedResourcesArr = createSelector(
  [selectTaskResourcesState],
  taskResources => taskResources.thisStageUsedResourcesArr,
);

export const selectAvailablePlannedTaskResources = createSelector(
  [selectTaskResourcesState],
  ({
    availableResourcesArr,
    plannedResources: { updatedResourcesArr },
  }): ITaskAvailableResourceOption[] => {
    const takenResourcesArr = updatedResourcesArr;
    return getResourceOptions(availableResourcesArr, takenResourcesArr);
  },
);

export const selectUpdatedUsedTaskResourcesState = createSelector(
  [selectTask],
  state => state.taskResources.usedResources.updatedResourcesArr,
);

export const selectUsedTaskResourcesArrLength = createSelector(
  [selectTask],
  state => state.taskResources.usedResources.resourcesArr.length,
);

export const selectAvailableThisStageUsedTaskResources = createSelector(
  [selectTaskResourcesState],
  ({
    availableResourcesArr,
    thisStageUsedResourcesArr,
  }): ITaskAvailableResourceOption[] => {
    const takenResourcesArr = thisStageUsedResourcesArr;
    return getResourceOptions(availableResourcesArr, takenResourcesArr);
  },
);

export const selectAvailableUsedTaskResources = createSelector(
  [selectTaskResourcesState],
  ({
    availableResourcesArr,
    usedResources: { updatedResourcesArr },
  }): ITaskAvailableResourceOption[] => {
    const takenResourcesArr = updatedResourcesArr;
    return getResourceOptions(availableResourcesArr, takenResourcesArr);
  },
);

export const selectTaskUsedResourcesGroupedByCompaniesState = createSelector(
  [selectTask],
  (state): ITaskResourceGroupedByCompanyItem[] => {
    return state.taskResources.usedResources.resourcesArr.reduce(
      (accumulator: ITaskResourceGroupedByCompanyItem[], resource) => {
        const itemIdx = accumulator.findIndex(
          item => item?.company?.id === resource.company?.id,
        );
        if (itemIdx !== -1) {
          const resourceIdx = accumulator[itemIdx].resources.findIndex(
            accResource => accResource.resourceId === resource.resourceId,
          );
          if (resourceIdx !== -1) {
            accumulator[itemIdx].resources[resourceIdx].totalAmount +=
              resource.amount || 0;
          } else {
            accumulator[itemIdx].resources.push({
              resourceId: resource.resourceId,
              name: resource.name,
              totalAmount: resource.amount || 0,
            });
          }
        } else if (resource.company) {
          accumulator.push({
            company: {
              id: resource.company.id,
              name: resource.company.name,
            },
            resources: [
              {
                resourceId: resource.resourceId,
                name: resource.name,
                totalAmount: resource.amount || 0,
              },
            ],
          });
        }

        return accumulator;
      },
      [],
    );
  },
);

export const selectPlannedTaskResourcesUpdates = createSelector(
  [selectTaskResourcesState],
  ({ plannedResources: { resourcesArr, updatedResourcesArr } }) => {
    const newResourcesArr = updatedResourcesArr.filter(
      updatedResource =>
        !resourcesArr.find(
          plannedResource =>
            plannedResource.resourceId === updatedResource.resourceId,
        ),
    );

    const deletedResourcesArr = resourcesArr.filter(
      plannedResource =>
        !updatedResourcesArr.find(
          updatedResource =>
            updatedResource.resourceId === plannedResource.resourceId,
        ),
    );

    const changedResources = updatedResourcesArr.filter(plannedResource => {
      const updatedResource = resourcesArr.find(
        updatedResource =>
          updatedResource.resourceId === plannedResource.resourceId,
      );

      if (updatedResource && updatedResource.norm !== plannedResource.norm) {
        return true;
      }
      return false;
    });

    return {
      newResourcesArr,
      deletedResourcesArr,
      updatedResourcesArr: changedResources,
    };
  },
);

export const selectRoutesWithoutTaskFilters = createSelector(
  [selectTask],
  state => state.routesWithoutTaskFilters,
);

export const selectCheckedRouteIds = createSelector(
  [selectTask],
  state => state.checkedRouteIds,
);
